/**
 * Access — a company's administrators manage their own people. The sign-in
 * policy (code sign-in, allowed domains) is shown read-only: EVT sets it.
 */
import { Notice, Spinner } from '@evt/flow-stream-ui'
import { useQuery } from '@tanstack/react-query'
import { getAccessTenant } from '../api/access'
import { useSession } from '../auth/useSession'
import { AccessPanel, accessKey } from '../components/access/AccessPanel'
import { describeApiError, useI18n } from '../i18n'

export function AccessPage() {
  const { t } = useI18n()
  const { contractor, permissions, platformAdmin, strongAuth } = useSession()
  const tenantId = contractor?.id ?? ''
  const allowed = permissions.includes('access:manage') || platformAdmin
  const tenant = useQuery({
    queryKey: accessKey(tenantId, 'tenant'),
    queryFn: () => getAccessTenant(tenantId),
    enabled: Boolean(tenantId) && allowed && strongAuth
  })

  return (
    <div className="page portal-page access-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">{t.access.title}</h1>
          <div className="page-sub">{t.access.subtitle(contractor?.name ?? '')}</div>
        </div>
      </div>
      {!allowed ? (
        <Notice kind="warning">{t.errors.accessForbidden}</Notice>
      ) : !strongAuth ? (
        <Notice kind="info">{t.access.stepUp}</Notice>
      ) : tenant.isLoading ? (
        <Spinner size={16} />
      ) : tenant.error ? (
        <Notice kind="danger">{describeApiError(t, tenant.error)}</Notice>
      ) : (
        <>
          <section className="card access-policy">
            <div className="card-head">
              <div className="card-title">{t.access.policyTitle}</div>
            </div>
            <div className="card-pad">
              <div>{tenant.data?.settings.emailCodeLogin ? t.access.policyEmailCode : t.access.policyPasskey}</div>
              <div>
                {(tenant.data?.settings.allowedEmailDomains.length ?? 0) > 0
                  ? t.access.policyDomains(tenant.data?.settings.allowedEmailDomains.join(', ') ?? '')
                  : t.access.policyDomainsAny}
              </div>
              <div className="muted access-help">{t.access.policyManagedByEvt}</div>
            </div>
          </section>
          <AccessPanel tenantId={tenantId} tenant={tenant.data} />
        </>
      )}
    </div>
  )
}
